/******************************************************************************
 * AdminBackupsToolsTab: browse and download the database backups uploaded from
 * technicians' phones. Endpoints (core):
 *   GET admin/technician_devices                       -> list registered devices
 *   GET users/technician_devices/{techId}/backups      -> a technician's backups
 *   GET users/technician_devices/{backupId}/download   -> backup zip (arraybuffer)
 *****************************************************************************/

/* Imports */
import { useEffect, useMemo, useState } from "react";
import { Card, CardBody, IconButton, Typography } from "@material-tailwind/react";
import Select_ from "react-select";
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import { saveAs } from "file-saver";
import toast from "react-hot-toast";
import { t } from "i18next";
/***/

/* Components */
import Loading from "../../../../../../components/loading/Loading";
import { Table } from "../../../../../../components/table/Table";
/***/

/* Services */
import { api } from "../../../../../../services/api/api";
import { showApiErrorToast } from "../../../../../../services/api/error";
/***/

/**
* AdminBackupsToolsTab: device backups administration
*/
export default function AdminBackupsToolsTab() {
  const [devices, setDevices] = useState<EasyTech.TechnicianDevice[] | null>(null);
  const [selectedTech, setSelectedTech] = useState<string>("");
  const [backups, setBackups] = useState<EasyTech.DeviceBackup[] | null>(null);
  const [loadingBackups, setLoadingBackups] = useState<boolean>(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  useEffect(() => {
    api.get("admin/technician_devices")
      .then((ret) => setDevices(ret as EasyTech.TechnicianDevice[]))
      .catch((err) => {
        console.error(err);
        setDevices([]);
      });
  }, []);

  // Unique technicians derived from the registered devices.
  const techOptions = useMemo(() => {
    const map = new Map<string, string>();
    (devices || []).forEach((device) => {
      const tech = device.technician;
      if (tech && !map.has(tech.id)) {
        map.set(tech.id, `${tech.firstName || ""} ${tech.lastName || ""}`.trim() || tech.email || tech.id);
      }
    });
    return Array.from(map.entries()).map(([value, label]) => ({ value, label }));
  }, [devices]);

  /**
  * loadBackups: fetch the selected technician's backups
  * @param techId - technician id
  */
  const loadBackups = (techId: string) => {
    setSelectedTech(techId);
    setBackups(null);
    if (!techId) return;

    setLoadingBackups(true);
    api.get("users/technician_devices/" + techId + "/backups")
      .then((ret) => setBackups(ret as EasyTech.DeviceBackup[]))
      .catch((err) => {
        console.error(err);
        showApiErrorToast(err, { fallbackMessage: t("admin:tools.backups.loadError") });
        setBackups([]);
      })
      .finally(() => setLoadingBackups(false));
  };
  /***/

  /**
  * download: download a backup zip
  * @param backup - the backup to download
  */
  const download = (backup: EasyTech.DeviceBackup) => {
    setDownloadingId(backup.id);
    api.getPhotos("users/technician_devices/" + backup.id + "/download")
      .then((buffer) => {
        const name = (backup.bucketFile || `backup-${backup.id}`).split("/").pop() as string;
        const fileName = name.endsWith(".zip") ? name : `${name}.zip`;
        saveAs(new Blob([buffer], { type: "application/zip" }), fileName);
      })
      .catch((err) => {
        console.error(err);
        showApiErrorToast(err, { fallbackMessage: t("admin:tools.backups.downloadError") });
      })
      .finally(() => setDownloadingId(null));
  };
  /***/

  const TABLE_HEAD = [
    { label: t("admin:tools.backups.file"), orderKey: null },
    { label: t("common:actions"), orderKey: null, width: "80px" }
  ];

  return (
    <div className="my-4 flex flex-col gap-6">
      <Card className="border">
        <CardBody className="flex flex-col gap-3">
          <Typography variant="h6">{t("admin:tools.backups.title")}</Typography>
          <Typography variant="small" className="text-gray-600">
            {t("admin:tools.backups.notice")}
          </Typography>

          {devices === null ?
            <Loading size="h-5 w-5" />
            :
            <Select_
              placeholder={t("admin:tools.backups.selectTechnician")}
              options={techOptions}
              value={techOptions.find((o) => o.value === selectedTech) || null}
              onChange={(opt) => loadBackups(opt ? opt.value : "")} />}
        </CardBody>
      </Card>

      {selectedTech && (
        loadingBackups ?
          <Loading />
          :
          <Table tableHead={TABLE_HEAD}
            emptyTable={(!backups || backups.length === 0) ? t("admin:tools.backups.empty") : false}>
            {(backups || []).map((backup) => (
              <tr key={backup.id} className="border-b last:border-b-0">
                <td className="p-3">
                  <Typography variant="small">{backup.bucketFile || backup.id}</Typography>
                </td>
                <td className="p-3">
                  {downloadingId === backup.id ?
                    <Loading size="h-4 w-4" />
                    :
                    <IconButton variant="text" size="sm" onClick={() => download(backup)}>
                      <ArrowDownTrayIcon className="w-4" />
                    </IconButton>}
                </td>
              </tr>
            ))}
          </Table>
      )}
    </div>
  );
}
/***/
